import { useState } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { LuFolder, LuCornerUpLeft, LuChevronUp } from 'react-icons/lu'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import type { FileEntry, UploadState } from '../models/useFiles'
import { encodeDavPath, normaliseDavPath } from '../lib/webdavClient'
import { formatBytes, splitName } from '../utilities/general'
import { fileListSx, fileRowSx, modifiedLabel, modifiedTitle } from '../utilities/fileList'
import { getFileIcon } from '../utilities/fileIcons'
import FileRowMenu from './FileRowMenu'
import TextFieldForm from './TextFieldForm'

type Props = {
  entries: FileEntry[]
  basePath: string
  currentPath: string
  onFileClick: (entry: FileEntry) => void
  onRename: (entry: FileEntry, newBasename: string) => void | Promise<void>
  onDelete: (entry: FileEntry) => void | Promise<void>
  state?: LinkProps['state']
  uploads?: UploadState[]
}

// Columns (app#328): Modified and the overflow menu always show. Size shows only when the list
// is at least SIZE_MIN_WIDTH_PX (420px) wide, so a narrow drawer hides Size first. The rule is
// `fileRowSx` in `utilities/fileList.ts`.
function FilenameCell({
  basename,
  splitExt = true,
  italic,
}: {
  basename: string
  splitExt?: boolean
  italic?: boolean
}) {
  const [stem, ext] = splitExt ? splitName(basename) : [basename, '']
  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', mr: 1, fontStyle: italic ? 'italic' : undefined }}>
      <Typography noWrap variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {stem}
      </Typography>
      {ext && (
        <Typography variant="body2" sx={{ flexShrink: 0 }}>
          {ext}
        </Typography>
      )}
    </Box>
  )
}

const rowSx = fileRowSx
const iconSx = { minWidth: 26, color: 'contrast.medium' }

export default function FileList({
  entries,
  basePath,
  currentPath,
  onFileClick,
  onRename,
  onDelete,
  state,
  uploads,
}: Props) {
  const [editing, setEditing] = useState<string | null>(null)

  // Safety net: drop the PROPFIND self-entry if the webdav client didn't filter it.
  const currentNorm = normaliseDavPath(currentPath)
  const sorted = [...entries]
    .filter(e => normaliseDavPath(e.filename) !== currentNorm && e.basename !== '' && e.basename !== '.')
    .sort((a, b) => {
      if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1
      return a.basename.localeCompare(b.basename)
    })

  const atRoot = !currentPath || currentPath === '/'
  let parentTo: string | null = null
  if (!atRoot) {
    const segs = currentPath.split('/').filter(Boolean)
    segs.pop()
    parentTo = segs.length ? `${basePath}/${encodeDavPath(segs.join('/'))}` : basePath
  }

  if (!sorted.length && !uploads?.length && atRoot) {
    return (
      <Typography
        color="textSecondary"
        sx={{
          textAlign: 'center',
          marginY: 4
        }}>This folder is empty.
              </Typography>
    )
  }

  const startRename = (entry: FileEntry) => setEditing(entry.filename)
  const cancelRename = () => setEditing(null)
  const commitRename = async (entry: FileEntry, value: string) => {
    setEditing(null)
    const next = value.trim()
    if (next && next !== entry.basename) await onRename(entry, next)
  }

  return (
    <List disablePadding sx={fileListSx}>
      {uploads && uploads.length > 0 && <Typography variant="h6">Uploading</Typography>}
      {uploads?.map(u => {
        const pct = u.bytesTotal > 0 ? Math.min(100, (u.bytesUploaded / u.bytesTotal) * 100) : 0
        return (
          <ListItem key={u.id} sx={rowSx}>
            <ListItemIcon sx={iconSx}>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={16}
                  thickness={5}
                  sx={{ color: theme => theme.palette.action.disabledBackground }}
                />
                <CircularProgress
                  variant="determinate"
                  value={pct}
                  size={16}
                  thickness={5}
                  sx={{ position: 'absolute', left: 0 }}
                />
              </Box>
            </ListItemIcon>
            <FilenameCell basename={u.basename} italic />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: 'primary.main' }}>
              <LuChevronUp />
              <Typography variant="caption" sx={{
                color: 'inherit'
              }}>
                {formatBytes(u.bytesUploaded)}
              </Typography>
            </Box>
          </ListItem>
        )
      })}
      {parentTo !== null && (
        <ListItemButton component={Link} to={parentTo} state={state} sx={rowSx}>
          <ListItemIcon sx={iconSx}>
            <LuCornerUpLeft />
          </ListItemIcon>
          <Typography sx={{ flex: 1 }} color="textSecondary">
            ..
          </Typography>
        </ListItemButton>
      )}
      {sorted.map(entry => {
        const Icon = entry.isDirectory ? LuFolder : getFileIcon(entry.basename)
        const icon = (
          <ListItemIcon sx={iconSx}>
            <Icon />
          </ListItemIcon>
        )

        if (editing === entry.filename) {
          return (
            <ListItem key={entry.filename} sx={rowSx}>
              {icon}
              <TextFieldForm
                autoFocus
                size="small"
                value={entry.basename}
                onSubmit={next => commitRename(entry, next)}
                onCancel={cancelRename}
              />
            </ListItem>
          )
        }

        const body = (
          <>
            {icon}
            <FilenameCell basename={entry.basename} splitExt={!entry.isDirectory} />
            <Typography
              className="row-date"
              variant="caption"
              color="textSecondary"
              title={modifiedTitle(entry.lastmod) || undefined}
            >
              {modifiedLabel(entry.lastmod)}
            </Typography>
            <Typography className="row-size" variant="caption" color="textSecondary">
              {entry.isDirectory ? '' : formatBytes(entry.size)}
            </Typography>
            <Box className="row-menu">
              <FileRowMenu
                name={entry.basename}
                isDirectory={entry.isDirectory}
                onRename={() => startRename(entry)}
                onDelete={() => onDelete(entry)}
              />
            </Box>
          </>
        )
        if (entry.isDirectory) {
          const to = `${basePath}/${encodeDavPath([...currentPath.split('/').filter(Boolean), entry.basename].join('/'))}`
          return (
            <ListItemButton key={entry.filename} component={Link} to={to} state={state} sx={rowSx}>
              {body}
            </ListItemButton>
          )
        }
        return (
          <ListItemButton key={entry.filename} onClick={() => onFileClick(entry)} sx={rowSx}>
            {body}
          </ListItemButton>
        )
      })}
    </List>
  )
}
